// 분리된 서비스들을 통합하는 메인 파일
import { restaurantCrudService } from './restaurant-crud.service';
import { restaurantQueryService } from './restaurant-query.service';
import { restaurantSearchService } from './restaurant-search.service';
import { restaurantAvailabilityService } from './restaurant-availability.service';

// 타입 정의를 re-export
export type { 
  Restaurant,
  RestaurantWithProducts, 
  RestaurantCreate, 
  RestaurantUpdate 
} from './restaurant.types';

class RestaurantService {
  // Query methods - 조회 관련
  async getRestaurants(params?: {
    latitude?: number;
    longitude?: number;
    radius?: number;
    is_open?: boolean;
    withProducts?: boolean;
    offerType?: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  }) {
    return await restaurantQueryService.getRestaurants(params);
  }

  // 마감할인 탭 전용 - products/offers/stock 최소 필드 조회
  async getDeadlineRestaurantsWithOffers() {
    return await restaurantQueryService.getDeadlineRestaurantsWithOffers();
  }

  async getRestaurantById(id: string, withProducts: boolean = false) {
    return await restaurantQueryService.getRestaurantById(id, withProducts);
  }

  async getDeadlineDiscountRestaurants(userLocation?: { lat: number; lng: number }) {
    return await restaurantQueryService.getDeadlineDiscountRestaurants(userLocation);
  }

  async getReservationDiscountRestaurants(userLocation?: { lat: number; lng: number }) {
    return await restaurantQueryService.getReservationDiscountRestaurants(userLocation);
  }

  async getRegularOrderRestaurants(userLocation?: { lat: number; lng: number }) {
    return await restaurantQueryService.getRegularOrderRestaurants(userLocation);
  }

  // CRUD methods - 생성/수정/삭제 관련
  async createRestaurant(restaurantData: any) {
    return await restaurantCrudService.createRestaurant(restaurantData);
  }

  async updateRestaurant(id: string, updates: any) {
    return await restaurantCrudService.updateRestaurant(id, updates);
  }

  async toggleRestaurantStatus(id: string, isOpen: boolean) {
    return await restaurantCrudService.toggleRestaurantStatus(id, isOpen);
  }

  async getRestaurantsByBusiness(businessId: string) {
    return await restaurantCrudService.getRestaurantsByBusiness(businessId);
  }

  // Search methods - 검색 관련
  async searchRestaurants(searchTerm: string, filters?: any) {
    return await restaurantSearchService.searchRestaurants(searchTerm, filters);
  }

  // Availability and Image methods - 가용성 및 이미지 관련
  async migrateRestaurantsToBusinessStructure() {
    return await restaurantAvailabilityService.migrateRestaurantsToBusinessStructure();
  }

  async getRestaurantImages(restaurantId: string) {
    return await restaurantAvailabilityService.getRestaurantImages(restaurantId);
  }

  async addRestaurantImage(restaurantId: string, imageFile: File, userId?: string, description?: string) {
    return await restaurantAvailabilityService.addRestaurantImage(restaurantId, imageFile, userId, description);
  }

  async deleteRestaurantImage(imageId: string) {
    return await restaurantAvailabilityService.deleteRestaurantImage(imageId);
  }

  async updateRestaurantImageSequence(imageId: string, newSequence: number) {
    return await restaurantAvailabilityService.updateRestaurantImageSequence(imageId, newSequence);
  }

  async getMainRestaurantImage(restaurantId: string) {
    return await restaurantAvailabilityService.getMainRestaurantImage(restaurantId);
  }

  async getRestaurantWithImages(id: string) {
    return await restaurantAvailabilityService.getRestaurantWithImages(
      id,
      (id: string, withProducts: boolean) => this.getRestaurantById(id, withProducts)
    );
  }
}

export const restaurantService = new RestaurantService();
